import { useMemo } from "react";

import { useLabels } from "~/hooks/useLabels";
import { useBreakpoints } from "~/hooks/useBreakpoints";

import { SheetModal } from "../sheetModal/SheetModal";
import { DialogModal } from "../dialogModal/DialogModal";
import { Button } from "../button/Button";
import { Box } from "../box/Box";
import { Text } from "../text/Text";
import { Entity } from "../entity/Entity";
import { Divider } from "../divider/Divider";

const DEFAULT_LABELS = {
  "common.cta.close": "Close",
  "wordDefinition.title": "Word Definition",
  "wordDefinition.language.hebrew": "Hebrew",
  "wordDefinition.language.greek": "Greek",
  "wordDefinition.language.aramaic": "Aramaic",
  "wordDefinition.language.latin": "Latin",
  "wordDefinition.definition": "Definition",
  "wordDefinition.original": "Original",
  "wordDefinition.transliteration": "Transliteration",
};

export interface WordDefinitionData {
  original: string;
  language: "hebrew" | "greek" | "aramaic" | "latin";
  transliteration: string;
  definition: string;
}

export interface WordDefinitionModalProps {
  isOpen?: boolean;
  definition?: WordDefinitionData;
  onRequestClose?: () => void;
}

export function WordDefinitionModal({
  isOpen,
  definition,
  onRequestClose,
}: WordDefinitionModalProps) {
  const { t } = useLabels({ fallback: DEFAULT_LABELS });

  const breakpoints = useBreakpoints();

  const languageLabel = useMemo(() => {
    switch (definition?.language) {
      case "hebrew":
        return t("wordDefinition.language.hebrew");
      case "greek":
        return t("wordDefinition.language.greek");
      case "aramaic":
        return t("wordDefinition.language.aramaic");
      case "latin":
        return t("wordDefinition.language.latin");
      default:
        return t("wordDefinition.language.hebrew");
    }
  }, [definition?.language, t]);

  const wordOriginal = useMemo(() => {
    switch (definition?.language) {
      case "hebrew":
        return <bp-text-hebrew>{definition?.original}</bp-text-hebrew>;
      case "greek":
        return <bp-text-greek>{definition?.original}</bp-text-greek>;
      case "aramaic":
        return <bp-text-hebrew>{definition?.original}</bp-text-hebrew>;
      case "latin":
      default:
        return <>{definition?.original}</>;
    }
  }, [definition?.language, definition?.original]);

  const content = useMemo(() => {
    return (
      <>
        <Entity title={t("wordDefinition.title")} postTitle={languageLabel} />

        <Divider />

        <Box direction="column" gap={12}>
          <div>
            <Text size="sm" weight="semibold">
              {t("wordDefinition.transliteration")}
            </Text>
            <Text size="md">{definition?.transliteration}</Text>
          </div>
          <div>
            <Text size="sm" weight="semibold">
              {t("wordDefinition.original")}
            </Text>
            <Text size="md">{wordOriginal}</Text>
          </div>
          <div>
            <Text size="sm" weight="semibold">
              {t("wordDefinition.definition")}
            </Text>
            <Text size="md" style={{ fontStyle: "italic" }}>
              {definition?.definition}
            </Text>
          </div>
        </Box>

        <Box
          style={{ marginBlockStart: "var(--size-2)" }}
          direction="column"
          gap={16}
        >
          <Button appearance="secondary" fill onClick={onRequestClose}>
            {t("common.cta.close")}
          </Button>
        </Box>
      </>
    );
  }, [
    definition?.definition,
    definition?.transliteration,
    languageLabel,
    onRequestClose,
    t,
    wordOriginal,
  ]);

  return breakpoints.lg ? (
    <DialogModal isOpen={isOpen} size="sm" onRequestClose={onRequestClose}>
      <DialogModal.Body>{content}</DialogModal.Body>
    </DialogModal>
  ) : (
    <SheetModal isOpen={isOpen} onRequestClose={onRequestClose}>
      <SheetModal.Body>{content}</SheetModal.Body>
    </SheetModal>
  );
}
